import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'
import { useNavigate } from 'react-router'

import { useRegisterDirtyForm } from '@/domains/companies'
import { reportError } from '@/domains/observability'

import { createInvoice } from './api'
import {
  type InvoiceFormAction,
  type InvoiceFormDerived,
  type InvoiceFormState,
  type LineItemDraft,
  InvoiceFormContext,
  PAYMENT_TERMS
} from './invoice-form-context'
import { INVOICES_KEY } from './keys'

function newLineItem(): LineItemDraft {
  return {
    key: crypto.randomUUID(),
    description: '',
    quantity: '1',
    unitPrice: '0'
  }
}

function addDays(isoDate: string, days: number): string {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return isoDate
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

/** "user touched anything." used by the dirty-form registry so the
 *  company switcher prompts before re-scoping a half-edited invoice.
 *  the comparison is against the initialState defaults: paymentTerm,
 *  currency, etc. count as dirty only if the user changed them, not
 *  just because they picked the same value the form opened with. */
function isDirty(state: InvoiceFormState): boolean {
  if (state.recipient !== null) return true
  if (state.purchaseOrderNumber !== '') return true
  if (state.notes !== '') return true
  if (state.taxAmount !== '0') return true
  if (state.shippingAmount !== '0') return true
  if (state.paymentTerm !== 'net_30') return true
  if (state.currency !== 'usd') return true
  /** one default line item ships in initialState. "dirty" means the user typed
   * into any of its fields, or added/removed rows.
   */
  if (state.lineItems.length !== 1) return true
  const sole = state.lineItems[0]
  if (
    sole &&
    (sole.description !== '' || sole.quantity !== '1' || sole.unitPrice !== '0')
  ) {
    return true
  }
  return false
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function initialState(): InvoiceFormState {
  return {
    recipient: null,
    purchaseOrderNumber: '',
    dueAt: addDays(todayIso(), 30),
    paymentTerm: 'net_30',
    currency: 'usd',
    notes: '',
    taxAmount: '0',
    shippingAmount: '0',
    lineItems: [newLineItem()],
    submitting: false,
    error: ''
  }
}

function reducer(
  state: InvoiceFormState,
  action: InvoiceFormAction
): InvoiceFormState {
  switch (action.type) {
    case 'SET_RECIPIENT':
      return { ...state, recipient: action.payload }
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'SET_DUE_DATE':
      // a manual due-date edit detaches from the preset term.
      return { ...state, dueAt: action.value, paymentTerm: 'custom' }
    case 'SET_TERM': {
      /** terms run from today, not from the invoice's issue date: the server
       *  stamps that on send, so at compose time it does not exist yet. */
      const term = PAYMENT_TERMS.find(t => t.value === action.value)
      return {
        ...state,
        paymentTerm: action.value,
        dueAt: term ? addDays(todayIso(), term.days) : state.dueAt
      }
    }
    case 'ADD_LINE_ITEM':
      return { ...state, lineItems: [...state.lineItems, newLineItem()] }
    case 'UPDATE_LINE_ITEM':
      return {
        ...state,
        lineItems: state.lineItems.map(item =>
          item.key === action.key ? { ...item, ...action.patch } : item
        )
      }
    case 'REMOVE_LINE_ITEM':
      return {
        ...state,
        lineItems:
          state.lineItems.length === 1
            ? state.lineItems
            : state.lineItems.filter(item => item.key !== action.key)
      }
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: '' }
    case 'SET_ERROR':
      return { ...state, submitting: false, error: action.message }
    default:
      return state
  }
}

function toNumber(value: string): number {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function derive(state: InvoiceFormState): InvoiceFormDerived {
  const subtotal = state.lineItems.reduce(
    (sum, item) => sum + toNumber(item.quantity) * toNumber(item.unitPrice),
    0
  )
  const taxAmount = toNumber(state.taxAmount)
  const shippingAmount = toNumber(state.shippingAmount)
  const hasValidLineItem = state.lineItems.some(
    item => item.description.trim() !== '' && toNumber(item.quantity) > 0
  )
  const isValid =
    state.recipient !== null && state.dueAt !== '' && hasValidLineItem
  return {
    subtotal,
    taxAmount,
    shippingAmount,
    total: subtotal + taxAmount + shippingAmount,
    isValid
  }
}

export function InvoiceFormProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, undefined, initialState)
  const derived = derive(state)
  /** register with the dirty-form registry so the company switcher confirm-dialog
   * gate triggers on a half-filled invoice draft.
   */
  useRegisterDirtyForm('invoice-form', isDirty(state))

  const mutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: async invoice => {
      await queryClient.invalidateQueries({ queryKey: INVOICES_KEY })
      void navigate(`/invoices/${invoice.id}`)
    },
    onError: error => {
      reportError(error)
      dispatch({
        type: 'SET_ERROR',
        message: 'Failed to save invoice. Check the fields and try again.'
      })
    }
  })

  function submit(action: 'draft' | 'send') {
    if (!derived.isValid || !state.recipient) {
      dispatch({
        type: 'SET_ERROR',
        message: 'Pick a recipient and add at least one line item.'
      })
      return
    }
    dispatch({ type: 'SUBMIT_START' })
    mutation.mutate({
      recipientCompanyId: state.recipient.companyId,
      purchaseOrderNumber: state.purchaseOrderNumber.trim() || null,
      dueAt: state.dueAt,
      currency: state.currency,
      notes: state.notes.trim() || null,
      taxAmount: derived.taxAmount,
      shippingAmount: derived.shippingAmount,
      lineItems: state.lineItems
        .filter(item => item.description.trim() !== '')
        .map(item => ({
          description: item.description.trim(),
          quantity: toNumber(item.quantity),
          unitPrice: toNumber(item.unitPrice)
        })),
      action
    })
  }

  return (
    <InvoiceFormContext.Provider value={{ state, derived, dispatch, submit }}>
      {children}
    </InvoiceFormContext.Provider>
  )
}
